// @spec AST-STR-048
'use client';

import { useOrganization } from '@clerk/clerk-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import {
  CreateSubcategoryDialog,
  DeleteSubcategoryDialog,
  EditSubcategoryDialog,
  SubcategoryList,
} from '@/features/settings/assets';
import type { AssetCategory, AssetSubcategory } from '@/features/settings/assets';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/settings/asset-categories')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.assetCategories'),
  }),
  component: AssetCategoriesPage,
});

/**
 * Asset categories management page.
 *
 * Shows list of asset subcategories grouped by category and allows admins
 * to create, edit, and delete subcategories.
 */
function AssetCategoriesPage() {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { isAdmin, isLoading: isRoleLoading } = useOrgRole();

  // Dialog state
  const [createCategory, setCreateCategory] = useState<AssetCategory | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<AssetSubcategory | null>(null);
  const [deletingSubcategory, setDeletingSubcategory] = useState<AssetSubcategory | null>(null);

  // Redirect non-admins to dashboard
  useEffect(() => {
    if (!isRoleLoading && !isAdmin) {
      void navigate({ to: '/dashboard' });
    }
  }, [isRoleLoading, isAdmin, navigate]);

  // Don't render content while checking permissions
  if (!isRoleLoading && !isAdmin) {
    return null;
  }

  // Get internal org ID from Clerk organization metadata
  const orgId = (organization?.publicMetadata as { internalId?: string } | undefined)?.internalId;

  // Show skeleton while loading org info
  if (isRoleLoading || !orgId) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-4xl space-y-6">
          <header>
            <Skeleton className="h-9 w-48" />
            <Skeleton className="h-5 w-80 mt-2" />
          </header>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-4xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('assetCategoriesPage.title')}
          </h1>
          <p className="mt-1 text-muted-foreground">{t('assetCategoriesPage.description')}</p>
        </header>

        <SubcategoryList
          onCreateClick={(category) => {
            setCreateCategory(category);
          }}
          onEditClick={(subcategory) => {
            setEditingSubcategory(subcategory);
          }}
          onDeleteClick={(subcategory) => {
            setDeletingSubcategory(subcategory);
          }}
          isAdmin={isAdmin}
        />

        {/* Create Dialog */}
        <CreateSubcategoryDialog
          category={createCategory}
          open={createCategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setCreateCategory(null);
            }
          }}
        />

        {/* Edit Dialog */}
        <EditSubcategoryDialog
          subcategory={editingSubcategory}
          open={editingSubcategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setEditingSubcategory(null);
            }
          }}
        />

        {/* Delete Dialog */}
        <DeleteSubcategoryDialog
          subcategory={deletingSubcategory}
          open={deletingSubcategory !== null}
          onOpenChange={(open) => {
            if (!open) {
              setDeletingSubcategory(null);
            }
          }}
        />
      </div>
    </div>
  );
}
